import { useEffect, useState } from 'react';
import type { UsageEvent, ApiResponse, ListUsageEventsResponse } from '@heybesafe/shared';
import { decryptUsageEventPayload } from '@heybesafe/shared';
import { getAdminInbox, getOrgEvents } from '../../api/client';
import { useFamilyKeys } from '../../context/FamilyKeysContext';
import { useSafe } from '../../context/SafeContext';
import { base64UrlToBytes } from '../../crypto/base64url';
import { analyzeEvents, type AnalysisResult } from './usageEventAnalysis';

const ANALYSIS_WINDOW_DAYS = 30;
const PAGE_LIMIT = 100;

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface UseUsageAnalysisResult {
  status: Status;
  analysis: AnalysisResult | null;
  error: Error | null;
}

export function useUsageAnalysis(filterOrgId: string): UseUsageAnalysisResult {
  const { orgs } = useSafe();
  const familyKeys = useFamilyKeys();
  const [status, setStatus] = useState<Status>('idle');
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus('loading');
      setError(null);
      try {
        const sinceIso = new Date(Date.now() - ANALYSIS_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
        const all: UsageEvent[] = [];
        let cursor: string | undefined = undefined;
        do {
          // Explicit annotation required — TS7022 fires on a conditional await with
          // distinct generic branches because the inferred type references itself.
          // See STATUS.md "Development learnings".
          const result: ApiResponse<ListUsageEventsResponse> = filterOrgId
            ? await getOrgEvents(filterOrgId, { since: sinceIso, limit: PAGE_LIMIT, nextToken: cursor })
            : await getAdminInbox({ since: sinceIso, limit: PAGE_LIMIT, nextToken: cursor });
          if (!result.ok) throw new Error(result.error);
          all.push(...result.data.events);
          cursor = result.data.nextToken;
          if (cancelled) return;
        } while (cursor);

        const plaintexts = new Map<string, { itemName: string; siteUrl: string } | null>();
        await Promise.all(
          all.map(async (event) => {
            const priv = familyKeys.getKey(event.orgId);
            const org = orgs.find((o) => o.id === event.orgId);
            if (!priv || !org?.publicKey) {
              plaintexts.set(event.id, null);
              return;
            }
            try {
              const pubBytes = base64UrlToBytes(org.publicKey);
              plaintexts.set(event.id, await decryptUsageEventPayload(event, priv, pubBytes));
            } catch {
              plaintexts.set(event.id, null);
            }
          })
        );

        if (cancelled) return;
        setAnalysis(analyzeEvents(all, plaintexts));
        setStatus('ready');
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterOrgId, familyKeys, orgs]);

  return { status, analysis, error };
}
